/**
 * Per-(user, tenant) Insights region-zone assignment — the region slots a given
 * user may open in a given tenant's reports. Server-backed (migrations 309/310)
 * and SUPER-ADMIN ONLY: mounted under /api/admin, which already applies
 * loadAppUserRoles + requireRole('super-admin'). See
 * server/src/routes/admin/insightsRegionZones.ts.
 *
 * STATE MODEL — the old mock's `undefined = unrestricted` sentinel is gone; the
 * real API is explicit:
 *   - { mode: 'unrestricted', regions: [] } — sees every slot, including slots
 *     added to the tenant's reports LATER (not a frozen snapshot). Default when
 *     no row exists.
 *   - { mode: 'restricted', regions: [...] } — allow-list of region slots. An
 *     empty list under 'restricted' means no region is viewable.
 *
 * Flipping to 'unrestricted' deliberately does NOT wipe the stored slots, so
 * flipping back restores the previous selection.
 *
 * NOT the UI gate: this is the AUTHORING side. The gate itself is
 * GET /api/analytics/client-reports/:id/{regions,content}, which resolves these
 * rows server-side before any bytes are served.
 */
import axiosClient from './axiosClient';

export type ZoneMode = 'unrestricted' | 'restricted';

export interface InsightsRegionZones {
  mode: ZoneMode;
  /** Region slots (raw values, case-sensitive — they must match report slots). */
  regions: string[];
}

const BASE = '/api/admin/insights-region-zones';

export const userTenantZonesApi = {
  get: async (userId: number, tenantCode: string): Promise<InsightsRegionZones> => {
    const { data } = await axiosClient.get(`${BASE}/${userId}/${encodeURIComponent(tenantCode)}`);
    return data;
  },

  set: async (
    userId: number,
    tenantCode: string,
    zones: InsightsRegionZones,
  ): Promise<InsightsRegionZones> => {
    const { data } = await axiosClient.put(
      `${BASE}/${userId}/${encodeURIComponent(tenantCode)}`,
      { mode: zones.mode, regions: zones.regions },
    );
    return data;
  },
};
